import { useState } from "react";
import { Sparkles } from "lucide-react";
import { toast } from "sonner";
import { brl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ResponsiveModal } from "@/components/ds/ResponsiveModal";

// Cor real do filamento: usa o hex cadastrado e, sem ele, um palpite pelo nome.
const NAME_COLORS = [
  [["branco", "off white", "perolado"], "#f4f4f5"],
  [["preto", "black"], "#18181b"],
  [["cinza", "prata", "grafite"], "#9ca3af"],
  [["amarelo", "ouro", "dourado"], "#facc15"],
  [["laranja"], "#f97316"],
  [["vermelho", "red"], "#dc2626"],
  [["rosa", "pink", "fucsia", "fúcsia"], "#ec4899"],
  [["roxo", "lilas", "lilás", "violeta", "purpura", "púrpura"], "#8b5cf6"],
  [["azul", "ciano", "turquesa"], "#3b82f6"],
  [["verde", "lima"], "#22c55e"],
  [["marrom", "cafe", "café", "bronze"], "#92400e"],
  [["bege", "areia", "nude"], "#e7d3b7"],
  [["transparente", "natural", "cristal"], "#e5e7eb"],
];

export const colorOf = (c) => {
  if (c.color_hex) return c.color_hex;
  const name = (c.name || "").toLowerCase();
  const hit = NAME_COLORS.find(([keys]) => keys.some((k) => name.includes(k)));
  return hit ? hit[1] : "#64748b";
};

function ColorPicker({ field, value, onPick, tid }) {
  return (
    <div className="flex flex-wrap gap-3">
      {(field.colors || []).map((c) => {
        const on = value === c.name;
        return (
          <button
            key={c.filament_id}
            type="button"
            onClick={() => onPick(on ? null : c)}
            data-testid={`custom-color-${tid}-${c.filament_id}`}
            className="flex w-16 flex-col items-center gap-1.5"
          >
            <span
              className={`h-10 w-10 rounded-full border transition-all ${
                on
                  ? "border-brand ring-2 ring-brand ring-offset-2 ring-offset-card"
                  : "border-white/25 hover:border-white/50"
              }`}
              style={{ backgroundColor: colorOf(c) }}
            />
            <span
              className={`w-full truncate text-center text-[10px] ${
                on ? "text-brand" : "text-muted-foreground"
              }`}
            >
              {c.name}
            </span>
          </button>
        );
      })}
    </div>
  );
}

/** Personalização em modal: mantém o card do catálogo compacto. */
export function CustomizeModal({ product, variation, price, onClose, onAdd }) {
  const fields = product.custom_fields || [];
  const [answers, setAnswers] = useState({});
  const tid = product.id;

  const submit = () => {
    const missing = fields.find((f) => f.required && !answers[f.name]?.value);
    if (missing) {
      toast.error(`Preencha "${missing.name}" para continuar.`);
      return;
    }
    onAdd(
      fields
        .map((f) => {
          const a = answers[f.name];
          return a?.value
            ? { name: f.name, value: a.value, filament_id: a.filament_id || null }
            : null;
        })
        .filter(Boolean)
    );
  };

  return (
    <ResponsiveModal
      open
      onOpenChange={(o) => !o && onClose()}
      title="Personalize seu produto"
      description={variation ? `${product.name} · ${variation.name}` : product.name}
      testid="customize-modal"
    >
      <div className="mt-2 flex w-full min-w-0 flex-col gap-5">
        <div className="max-h-[55vh] space-y-5 overflow-y-auto pr-1">
          {fields.map((f) => (
            <div key={f.name}>
              <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                {f.name}
                {f.required ? " *" : ""}
              </p>
              {f.type === "COR" && (
                <ColorPicker
                  field={f}
                  tid={tid}
                  value={answers[f.name]?.value}
                  onPick={(c) =>
                    setAnswers((prev) => ({
                      ...prev,
                      [f.name]: c ? { value: c.name, filament_id: c.filament_id } : { value: "" },
                    }))
                  }
                />
              )}
              {f.type === "SELECAO" && (
                <div className="flex flex-wrap gap-2">
                  {(f.options || []).map((opt) => {
                    const on = answers[f.name]?.value === opt;
                    return (
                      <button
                        key={opt}
                        type="button"
                        onClick={() =>
                          setAnswers((prev) => ({ ...prev, [f.name]: { value: on ? "" : opt } }))
                        }
                        data-testid={`custom-option-${tid}-${opt}`}
                        className={`rounded-full border px-3.5 py-2 text-xs transition-colors ${
                          on
                            ? "border-brand bg-brand/15 text-brand"
                            : "border-white/15 text-muted-foreground hover:text-foreground"
                        }`}
                      >
                        {opt}
                      </button>
                    );
                  })}
                </div>
              )}
              {f.type === "TEXTO" && (
                <>
                  <input
                    value={answers[f.name]?.value || ""}
                    maxLength={f.max_length}
                    onChange={(e) =>
                      setAnswers((prev) => ({ ...prev, [f.name]: { value: e.target.value } }))
                    }
                    data-testid={`custom-text-${tid}-${f.name}`}
                    placeholder={`Digite ${f.name.toLowerCase()}`}
                    className="h-11 w-full rounded-lg border border-white/15 bg-background px-3 text-sm outline-none transition-colors focus:border-brand"
                  />
                  <p className="mt-1 text-right text-[10px] text-muted-foreground">
                    {(answers[f.name]?.value || "").length}/{f.max_length}
                  </p>
                </>
              )}
            </div>
          ))}
        </div>

        {product.requires_approval && (
          <p className="text-[11px] text-muted-foreground">
            A produção começa depois que você aprovar a personalização pelo link do pedido.
          </p>
        )}

        <div className="flex items-center justify-between gap-3 border-t border-white/10 pt-4">
          <span className="font-num text-lg text-brand" data-testid="customize-price">
            {brl(price)}
          </span>
          <Button
            onClick={submit}
            data-testid="customize-add-btn"
            className="h-12 flex-1 gap-2 rounded-full font-semibold transition-transform active:scale-95"
          >
            <Sparkles className="h-4 w-4" /> Adicionar ao pedido
          </Button>
        </div>
      </div>
    </ResponsiveModal>
  );
}
